import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function logError(stage, message, extra = {}) {
  console.error(`[Push] ${stage}:`, message, extra);
  try {
    await axios.post(`${API}/push/debug-log`, {
      stage,
      message: String(message).slice(0, 500),
      user_agent: navigator.userAgent,
      is_standalone: window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true,
      extra,
    });
  } catch (_) {}
}

export const pushService = {
  isSupported: () => {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  },

  registerServiceWorker: async () => {
    if (!pushService.isSupported()) return null;
    try {
      const registration = await navigator.serviceWorker.register('/sw.js', { scope: '/' });
      console.log('[Push] Service Worker registered:', registration.scope);

      try { registration.update(); } catch (_) {}

      if (navigator.serviceWorker.controller) {
        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (refreshing) return;
          refreshing = true;
          setTimeout(() => window.location.reload(), 100);
        }, { once: true });
      }

      return registration;
    } catch (error) {
      await logError('sw-register', error.message);
      return null;
    }
  },

  getSubscription: async () => {
    const registration = await navigator.serviceWorker.ready;
    return registration.pushManager.getSubscription();
  },

  subscribe: async () => {
    if (!pushService.isSupported()) {
      throw new Error('Push notifications not supported on this device');
    }

    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS && !isStandalone) {
      throw new Error('En iOS tenés que instalar la app primero: tocá Compartir en Safari y elegí "Agregar a pantalla de inicio".');
    }

    console.log('[Push] 1/5 Fetching VAPID key...');
    let vapidPublicKey;
    try {
      const { data } = await axios.get(`${API}/push/vapid-public-key`);
      vapidPublicKey = data.publicKey;
    } catch (e) {
      await logError('vapid-fetch', e.message);
      throw new Error('No se pudo obtener la clave VAPID');
    }
    if (!vapidPublicKey) {
      await logError('vapid-missing', 'VAPID public key empty');
      throw new Error('Servidor sin VAPID configurado');
    }

    console.log('[Push] 2/5 Waiting for SW ready...');
    const registration = await navigator.serviceWorker.ready;

    console.log('[Push] 3/5 Reusing or creating subscription...');
    let subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      try { await subscription.unsubscribe(); } catch (_) {}
    }

    try {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });
    } catch (e) {
      await logError('pushmanager-subscribe', e.message, { vapidPrefix: vapidPublicKey.slice(0, 16) });
      throw new Error('No se pudo suscribir: ' + e.message);
    }

    console.log('[Push] 4/5 Sending subscription to backend...', subscription.endpoint.slice(0, 60));
    try {
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')))),
        },
      });
    } catch (e) {
      await logError('backend-save', e.message);
      throw new Error('No se pudo guardar la suscripción en el servidor');
    }

    console.log('[Push] 5/5 Done ✓');
    return subscription;
  },

  syncSubscription: async () => {
    if (!pushService.isSupported()) return false;
    if (Notification.permission !== 'granted') return false;
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return false;
      await axios.post(`${API}/push/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')))),
        },
      });
      return true;
    } catch (e) {
      console.warn('[Push] syncSubscription failed silently:', e?.message);
      return false;
    }
  },

  unsubscribe: async () => {
    const subscription = await pushService.getSubscription();
    if (subscription) {
      const endpoint = subscription.endpoint;
      try { await subscription.unsubscribe(); } catch (_) {}
      try { await axios.post(`${API}/push/unsubscribe`, endpoint ? { endpoint } : {}); } catch (_) {}
    }
    return true;
  },

  getPermissionStatus: () => {
    if (!pushService.isSupported()) return 'unsupported';
    return Notification.permission;
  },

  requestPermission: async () => {
    return Notification.requestPermission();
  },
};
